import {
  createContext,
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type FC,
} from 'react'
import HandshakeOutlined from '@mui/icons-material/HandshakeOutlined'
import LocalOfferOutlined from '@mui/icons-material/LocalOfferOutlined'
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom'
import {
  ButtonV2,
  LoadingLogo,
  PlanType,
  labravDefaultThemeV7 as theme,
} from '@labrav/react-components'
import { makeStyles } from 'tss-react/mui'
import {
  API,
  CustomNavButtonProp,
  LayoutWrapper,
  LayoutWrapperActionData,
  LayoutWrapperActions,
  LoadingTypes,
  NoHeaderLayoutWrapper,
  PageNotFound,
  EventScopeProvider,
  RouteApp,
  SelectedCloudIcon,
  crmServiceUrl,
  isLoading,
  useAccountId,
  useCurrentCloudProvider,
  useSalesforceContext,
  useUserType,
} from '@labrav/common'
import { isEmpty } from 'lodash'
import { FlyoutOffers } from '../components/FlyoutOffers'
import { FLYOUT_PRIVATE_OFFERS_LIST_SCOPE } from '../common/utils/hooks/usePrivateOfferCreationTracking'
import {
  ExternalPlatformType,
  ProductListingV2,
} from '../components/ProductListingV2'
import { ContractListingWrapper } from '../components/ContractListing/ContractListingWrapper'
import {
  ResellerAuthorizations,
  ViewReceivedResellerAuthorization,
  ViewSentResellerAuthorization,
} from '../components/ResellerAuthorizations'
import { FreeTrialLabraUI } from '../components/FreeTrialLabraUI'
import { CloudAndProductNameTitle } from '../components/CloudAndProductNameTitle'
import { RevenueInsightsWrapperV2 } from '../components/RevenueInsightsV2/RevenueInsightsWrapperV2'
import { RenewOfferSubscription } from '../components/Subscription/RenewOfferSubscription/RenewOfferSubscription'
import type { RenewOfferSubscriptionProps } from '../components/Subscription/RenewOfferSubscription/renewOfferTypes'
import { RenewChannelOfferSubscription } from '../components/Subscription/RenewChannelOfferSubscription/RenewChannelOfferSubscription'
import SubscriptionDetail, {
  type SubscriptionDetailProps,
} from '../components/Subscription/SubscriptionDetail/SubscriptionDetail'
import { SubscriptionsList } from '../components/Subscription/SubscriptionsList/SubscriptionsList'
import { AddEditProductListingWrapper } from '../components/AddEditProduct/AddEditProductListingWrapper'
import { DetailProductListing } from '../components/DetailProductListing/DetailProductListing'
import { Buyers } from '../components/Buyers/BuyersOverview'
import { BuyerDetails } from '../components/BuyerDetails/BuyersDetails'
import { BuyerHeader } from '../components/BuyerDetails/BuyerHeader/BuyerHeader'
import { FlyOutStorefrontV2 } from '../components/FlyoutStorefrontV2'
import {
  ButtonCreationFormikWrapper,
  FlyOutStorefrontTemplateWrapper,
} from '../components/FlyOutStorefront'
import { SearchRankingV2 } from '../components/SearchRanking/SearchRanking'
import { FlyOutUsageMetering } from '../components/FlyOutUsageMetering/FlyOutUsageMetering'
import {
  AddEditChannelOffer,
  ViewChannelOffer,
} from '../components/ChannelOffers'
import { useGetProductDetail } from '../common/utils/hooks/useGetProductDetail'
import { useSyncFlyoutRouteParams } from '../common/utils/hooks/useSyncFlyoutRouteParams'
import { AddEditResellerAuthorization } from '../components/AddEditResellerAuthorization/AddEditResellerAuthorization'
import { EditResaleAuthorizationWrapper } from '../components/oldResaleAuth/EditResaleAuthorizationWrapper/EditResaleAuthorizationWrapper'
import {
  EditPrivateOfferWrapper,
  EditPrivateOfferWrapperV2 as MarketplaceEditPrivateOfferWrapperV2,
  NewPrivateOfferWrapper,
} from '../components/NewPrivateOfferWrapper'
import {
  DetailedViewPrivateOfferWrapper as MarketplaceDetailedViewPrivateOfferWrapper,
  type DetailedViewPrivateOfferWrapperProps,
} from '../components/DetailedViewPrivateOfferWrapper'
import { MarketplaceMappingManager as MarketplaceMappingManagerUI } from '../components/MarketplaceMappingManager'
import { PartnerAccountDataProvider } from '../external/PartnerAccountDataProvider/PartnerAccountDataProvider'
import { customerFormData } from '../components/MarketplaceMappingManager/utils/customerFormData'
import { CRMTypes } from '../utils/constants'
import { useSelector } from '../store'
import crmConnectionImage from '../common/images/MarketplaceTemplate.svg'

const FlyoutMilestonesContext = createContext<FlyoutRouteScreen>(() => null)

export const RenewOfferSubscriptionWrapper = ({
  externalBaseURL,
  search,
  crmOpportunityId,
  crmObjectLink,
  isSalesforce,
  steps,
}: RenewOfferSubscriptionProps) => {
  return (
    <RenewOfferSubscription
      externalBaseURL={externalBaseURL}
      search={search}
      crmOpportunityId={crmOpportunityId}
      crmObjectLink={crmObjectLink}
      isSalesforce={isSalesforce}
      steps={steps}
    />
  )
}

export const SubscriptionDetailWrapper = ({
  isBack,
}: SubscriptionDetailProps) => <SubscriptionDetail isBack={isBack} />

export const ChannelOfferEditWrapper = () => {
  const Milestones = useContext(FlyoutMilestonesContext)
  const { cloud, accountId } = useParams<{
    cloud: string
    accountId: string
  }>()
  const navigate = useNavigate()

  const handleBack = useCallback(() => {
    if (cloud && accountId) {
      navigate(
        `/flyout/${cloud}/${accountId}/offers-authorizations/channel-offers`
      )
    } else {
      navigate('/flyout/offers-authorizations/channel-offers')
    }
  }, [cloud, accountId, navigate])

  return (
    <LayoutWrapper
      title={'Create a Channel offer'}
      component={AddEditChannelOffer}
      isSidePanelOpened={false}
      actions={Milestones}
      isBack
      onBack={handleBack}
    />
  )
}

export const EditPrivateOfferWrapperV2 = ({
  externalBaseURL,
  search,
  crmOpportunityId,
  crmObjectLink,
  isSidePanelVisible,
}: {
  externalBaseURL?: string
  search?: string
  crmOpportunityId?: string
  crmObjectLink?: string
  isSidePanelVisible?: boolean
}) => {
  const salesforceContext = useSalesforceContext()
  const isSalesforceContext =
    salesforceContext.error !== 'salesforce_context_not_available' &&
    !isEmpty(salesforceContext.partnerId)

  return (
    <MarketplaceEditPrivateOfferWrapperV2
      externalBaseURL={externalBaseURL}
      search={search}
      crmOpportunityId={crmOpportunityId}
      crmObjectLink={crmObjectLink}
      isSidePanelVisible={isSidePanelVisible}
      isSalesforceContext={isSalesforceContext}
    />
  )
}

export const CreateEditPrivateOfferWrapper = memo(() => {
  const {
    productId = '',
    cloud,
    accountId,
  } = useParams<{
    productId: string
    cloud: string
    accountId: string
  }>()
  const productFetchingLoading = useSelector(state =>
    isLoading(state.loading)(LoadingTypes.FLYOUT_PRODUCT_DETAIL)
  )
  const productsFetchingLoading = useSelector(state =>
    isLoading(state.loading)(LoadingTypes.FETCH_PRODUCTS)
  )
  const { search } = useLocation()
  const fromValue = new URLSearchParams(search).get('from') || ''
  const cloudMarketplace = useCurrentCloudProvider()
  const fallbackAccountId = useAccountId({ product: 'flyout' }) || ''
  const routeAccountId = accountId || fallbackAccountId
  const selectedProduct = useGetProductDetail({
    accountId: routeAccountId,
    productId,
    cloudMarketplace,
  })
  const backLink = useMemo(() => {
    return cloud && routeAccountId
      ? `/flyout/${cloud}/${routeAccountId}/offers-authorizations/private-offers`
      : '/flyout/offers-authorizations/private-offers'
  }, [cloud, routeAccountId])
  const PrivateOfferTitle = useCallback(
    () => <CloudAndProductNameTitle selectedProduct={selectedProduct} />,
    [selectedProduct]
  )
  const v2Offer = useMemo(() => {
    return (
      cloudMarketplace === 'AZURE' ||
      (cloudMarketplace === 'AWS' &&
        (selectedProduct?.productType === PlanType.CONTAINER ||
          selectedProduct?.productType === PlanType.SAAS_CONTRACT ||
          selectedProduct?.productType === PlanType.AMI ||
          selectedProduct?.productType === PlanType.SAAS_SUBSCRIPTION))
    )
  }, [cloudMarketplace, selectedProduct])

  if (
    productFetchingLoading ||
    productsFetchingLoading ||
    (cloudMarketplace === 'AWS' && !selectedProduct)
  ) {
    return <LoadingLogo />
  }

  return (
    <div data-testid="create-edit-wrapper">
      {v2Offer ? (
        <LayoutWrapper
          title={
            fromValue === 'clone'
              ? 'Clone private offer'
              : 'Create a private offer'
          }
          component={EditPrivateOfferWrapperV2}
          isBack={true}
          actions={SelectedCloudIcon}
          isSidePanelOpened={false}
          customNavButton={{
            ButtonName: 'Back to Private offers',
            link: backLink,
            shouldShowCustomNavButton: true,
          }}
        />
      ) : (
        <RouteApp
          titleIcon={<HandshakeOutlined fontSize="medium" />}
          title="New private offer"
          component={EditPrivateOfferWrapper}
          actions={PrivateOfferTitle}
        />
      )}
    </div>
  )
})

export const DetailedViewPrivateOfferWrapper = ({
  crmType,
  crmOpportunityId,
  externalBaseURL,
  search,
  onBack,
}: Omit<DetailedViewPrivateOfferWrapperProps, 'partnerId'>) => {
  const { partnerId } = useSalesforceContext()

  return (
    <MarketplaceDetailedViewPrivateOfferWrapper
      crmType={crmType}
      crmOpportunityId={crmOpportunityId}
      externalBaseURL={externalBaseURL}
      search={search}
      onBack={onBack}
      partnerId={partnerId}
    />
  )
}

const useCrmConnectionStyles = makeStyles()(theme => ({
  root: {
    display: 'flex',
    height: theme.spacing(32),
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(6),
    backgroundColor: theme.palette.text.white,
  },
  templateIcon: {
    height: theme.spacing(15.75),
    width: theme.spacing(21.5),
  },
  textContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  heading: {
    fontSize: theme.typography.subtitle1.fontSize,
    fontStyle: 'normal',
    fontWeight: 500,
    lineHeight: theme.typography.subtitle1.lineHeight,
    color: theme.palette.labraText.primary,
    fontFamily: theme.typography.fontFamily,
    letterSpacing: theme.typography.subtitle1.letterSpacing,
  },
  subHeading: {
    fontSize: theme.typography.body2.fontSize,
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: theme.typography.body2.lineHeight,
    color: theme.palette.labraText.description,
    fontFamily: theme.typography.fontFamily,
    letterSpacing: theme.typography.body2.letterSpacing,
  },
  button: {
    marginTop: theme.spacing(1),
  },
}))

const CrmConnectionPending = () => {
  const { classes } = useCrmConnectionStyles()
  const navigate = useNavigate()

  return (
    <div className={classes.root} data-testid="cosell-crm-page">
      <img
        src={crmConnectionImage}
        alt="crm image"
        className={classes.templateIcon}
        data-testid="co-sell-crm-image"
      />
      <div className={classes.textContainer} data-testid="empty-state-text">
        <div className={classes.heading}>Connect CRM Pending</div>
        <div className={classes.subHeading}>
          To get started with mapping manager or sync configurations, please
          connect your CRM by clicking on the button below.
        </div>
        <div className={classes.button}>
          <ButtonV2
            data-testid="connect-your-crm-button"
            styleType="primary"
            onClick={() => navigate('/settings/crm')}
          >
            Connect your CRM
          </ButtonV2>
        </div>
      </div>
    </div>
  )
}

type CrmAuthForm = {
  deleted_at?: string
  creds?: unknown[]
}

export const MarketplaceMappingManager = () => {
  const partnerType = useUserType()
  const crmId = useSelector<string>(
    state => state.AccountData[partnerType]?.partnerData?.crmId || ''
  )
  const { crmType } = customerFormData()
  const crmFormData = useSelector<CrmAuthForm | null | undefined>(state => {
    const authState = state as typeof state & {
      CRMAuth?: { crmFormData?: CrmAuthForm | null }
    }
    return authState.CRMAuth?.crmFormData
  })
  const [fetchedConnected, setFetchedConnected] = useState<boolean | null>(null)
  const isHubspot = crmType === CRMTypes.hubspot
  const storedConnected = crmFormData
    ? !isEmpty(crmFormData) &&
      (crmFormData.creds || []).length > 0 &&
      isEmpty(crmFormData.deleted_at)
    : null
  const isCrmConnected = storedConnected ?? fetchedConnected ?? false

  useEffect(() => {
    if (!isHubspot || !crmId || storedConnected !== null) {
      return
    }

    let cancelled = false
    API.get(`${crmServiceUrl}/crms/${crmId}?includes=creds,crm_app_creds`)
      .then(response => {
        if (cancelled) return
        const data = response?.data as CrmAuthForm | undefined
        setFetchedConnected(
          !isEmpty(data) &&
            (data?.creds || []).length > 0 &&
            isEmpty(data?.deleted_at)
        )
      })
      .catch(() => {
        if (!cancelled) setFetchedConnected(false)
      })

    return () => {
      cancelled = true
    }
  }, [crmId, isHubspot, storedConnected])

  return (
    <MarketplaceMappingManagerUI
      isCrmSelected={isHubspot ? isCrmConnected : true}
      crmConnectionPage={<CrmConnectionPending />}
    />
  )
}

export const ImpartnerProductListingWrapper = () => (
  <PartnerAccountDataProvider isExternal={true}>
    <ProductListingV2 externalPlatformType={ExternalPlatformType.IMPARTNER} />
  </PartnerAccountDataProvider>
)

export const ViewPrivateOfferDetails = () => {
  const { productId, accountId } = useParams<{
    productId: string
    accountId: string
  }>()
  const cloudMarketplace = useCurrentCloudProvider()
  const selectedProduct = useGetProductDetail({
    accountId: accountId || '',
    productId: productId || '',
    cloudMarketplace,
  })

  return cloudMarketplace === 'AZURE' ||
    (!(selectedProduct?.productType === PlanType.CONTAINER) &&
      cloudMarketplace === 'AWS') ? (
    <NoHeaderLayoutWrapper
      component={DetailedViewPrivateOfferWrapper}
      isSidePanelOpened={false}
    />
  ) : (
    <RouteApp
      titleIcon={<HandshakeOutlined fontSize="medium" />}
      title="New private offer"
      component={NewPrivateOfferWrapper}
      actions={CloudAndProductNameTitle}
    />
  )
}

export const AddEditResellerAuthorizationComponent = () => {
  useSyncFlyoutRouteParams()

  const cloudMarketplace = useCurrentCloudProvider()

  if (cloudMarketplace === 'AZURE' || cloudMarketplace === 'AWS') {
    return (
      <NoHeaderLayoutWrapper>
        <AddEditResellerAuthorization />
      </NoHeaderLayoutWrapper>
    )
  }

  return (
    <RouteApp
      titleIcon={<HandshakeOutlined fontSize="medium" />}
      title="New resale authorization"
      component={EditResaleAuthorizationWrapper}
      actions={CloudAndProductNameTitle}
    />
  )
}

const OFFERS_TITLE = 'Offers'

const FlyoutOffersRoute = () => (
  <EventScopeProvider metadata={FLYOUT_PRIVATE_OFFERS_LIST_SCOPE}>
    <FlyoutOffers />
  </EventScopeProvider>
)

export type FlyoutOnboardingRouteStatus = 'complete' | 'incomplete'

export type FlyoutRouteScreen = FC

export type FlyoutProductRoutesProps = {
  rootRedirectTo: string
  onboardingRoute: FlyoutOnboardingRouteStatus
  notOnboardedRoutes: FlyoutRouteScreen
  milestones: FlyoutRouteScreen
}

export const FlyoutProductRoutes = ({
  rootRedirectTo,
  onboardingRoute,
  notOnboardedRoutes: NotOnboardedRoutes,
  milestones: Milestones,
}: FlyoutProductRoutesProps) => {
  const navigate = useNavigate()
  const customNav = useMemo<CustomNavButtonProp>(
    () => ({
      shouldShowCustomNavButton: true,
    }),
    []
  )
  const flyOutStorefrontTemplateActions: LayoutWrapperActionData[] = [
    {
      key: 'selectedCloud',
      component: <SelectedCloudIcon />,
      text: '',
      type: 'component',
    },
  ]

  return (
    <FlyoutMilestonesContext.Provider value={Milestones}>
      <Routes>
        <Route path="/" element={<Navigate to={rootRedirectTo} />} />
        <Route
          path="onboarding/*"
          element={
            onboardingRoute === 'complete' ? (
              <Navigate to="/overview" />
            ) : (
              <div data-testid="onboarding">
                <NotOnboardedRoutes />
              </div>
            )
          }
        />
        <Route
          path=":cloud/:accountId/products-listing"
          element={
            <LayoutWrapper
              title={'Listings'}
              component={ProductListingV2}
              isSidePanelOpened={false}
              actions={Milestones}
              customNavButton={customNav}
            />
          }
        />
        {/* Redirect old route to new pattern - will be handled by ProductListingV2 component */}
        <Route
          path="products-listing"
          element={
            <LayoutWrapper
              title={'Listings'}
              component={ProductListingV2}
              isSidePanelOpened={false}
              actions={Milestones}
              customNavButton={customNav}
            />
          }
        />
        {/* New route for contracts with cloud and accountId */}
        <Route
          path=":cloud/:accountId/products-listing/:productId/contracts"
          element={<ContractListingWrapper />}
        />
        <Route
          path=":cloud/:accountId/products-listing/:productId/contracts/:contractId"
          element={<ContractListingWrapper />}
        />
        {/* Backward compatibility */}
        <Route
          path="products-listing/:productId/contracts"
          element={<ContractListingWrapper />}
        />
        <Route
          path="products-listing/:productId/contracts/:contractId"
          element={<ContractListingWrapper />}
        />

        {/* New route with cloud and accountId */}
        <Route
          path=":cloud/:accountId/products-listing/:productId/private-offers/edit"
          element={<CreateEditPrivateOfferWrapper />}
        />
        <Route
          path=":cloud/:accountId/products-listing/:productId/private-offers/:privateOfferId/edit"
          element={<CreateEditPrivateOfferWrapper />}
        />
        <Route
          path=":cloud/:accountId/products-listing/:productId/private-offers/:privateOfferId/details"
          element={<ViewPrivateOfferDetails />}
        />

        {/* Backward compatibility */}
        <Route
          path="products-listing/:productId/private-offers/edit"
          element={<CreateEditPrivateOfferWrapper />}
        />
        <Route
          path="products-listing/:productId/private-offers/:privateOfferId/edit"
          element={<CreateEditPrivateOfferWrapper />}
        />
        <Route
          path="products-listing/:productId/private-offers/:privateOfferId/details"
          element={<ViewPrivateOfferDetails />}
        />

        {/* New route with cloud and accountId */}
        <Route
          path=":cloud/:accountId/products-listing/:productId/resale-authorizations/edit"
          element={<AddEditResellerAuthorizationComponent />}
        />
        <Route
          path=":cloud/:accountId/products-listing/:productId/resale-authorizations/:resaleAuthorizationId/edit"
          element={<AddEditResellerAuthorizationComponent />}
        />
        <Route
          path=":cloud/:accountId/products-listing/:productId/resale-authorizations/:resaleAuthorizationId/details"
          element={
            <NoHeaderLayoutWrapper component={ViewSentResellerAuthorization} />
          }
        />

        {/* Backward compatibility */}
        <Route
          path="products-listing/:productId/resale-authorizations/edit"
          element={<AddEditResellerAuthorizationComponent />}
        />

        <Route
          path="products-listing/:productId/resale-authorizations/:resaleAuthorizationId/edit"
          element={<AddEditResellerAuthorizationComponent />}
        />

        <Route
          path="products-listing/:productId/resale-authorizations/:resaleAuthorizationId/details"
          element={
            <NoHeaderLayoutWrapper component={ViewSentResellerAuthorization} />
          }
        />
        {/* New route with cloud and accountId */}
        <Route
          path=":cloud/:accountId/resale-authorizations/received/:resaleAuthorizationId/details"
          element={
            <NoHeaderLayoutWrapper
              component={ViewReceivedResellerAuthorization}
            />
          }
        />
        {/* Backward compatibility */}
        <Route
          path="/resale-authorizations/received/:resaleAuthorizationId/details"
          element={
            <NoHeaderLayoutWrapper
              component={ViewReceivedResellerAuthorization}
            />
          }
        />

        {/* New route with cloud and accountId */}
        <Route
          path=":cloud/:accountId/products-listing/:productId/free-trial"
          element={
            <RouteApp
              titleIcon={<LocalOfferOutlined fontSize="medium" />}
              title="Public Free Trial"
              component={FreeTrialLabraUI}
              actions={CloudAndProductNameTitle}
            />
          }
        />

        {/* Backward compatibility */}
        <Route
          path="products-listing/:productId/free-trial"
          element={
            <RouteApp
              titleIcon={<LocalOfferOutlined fontSize="medium" />}
              title="Public Free Trial"
              component={FreeTrialLabraUI}
              actions={CloudAndProductNameTitle}
            />
          }
        />
        {/* New route with cloud and accountId */}
        <Route
          path=":cloud/:accountId/reseller-authorizations/*"
          element={
            <LayoutWrapper
              title="Reseller authorizations"
              component={ResellerAuthorizations}
              actions={SelectedCloudIcon}
              isSidePanelOpened={false}
            />
          }
        />
        {/* Backward compatibility */}
        <Route
          path="/reseller-authorizations/*"
          element={
            <LayoutWrapper
              title="Reseller authorizations"
              component={ResellerAuthorizations}
              actions={SelectedCloudIcon}
              isSidePanelOpened={false}
            />
          }
        />
        {/* New route with cloud and accountId */}
        <Route
          path=":cloud/:accountId/offers-authorizations/*"
          element={
            <LayoutWrapper
              title={OFFERS_TITLE}
              component={FlyoutOffersRoute}
              isSidePanelOpened={false}
              actions={Milestones}
              customNavButton={customNav}
            />
          }
        />
        {/* Backward compatibility */}
        <Route
          path="offers-authorizations/*"
          element={
            <LayoutWrapper
              title={OFFERS_TITLE}
              component={FlyoutOffersRoute}
              isSidePanelOpened={false}
              actions={Milestones}
              customNavButton={customNav}
            />
          }
        />

        {/* New revenue routes with cloud and accountId */}
        <Route
          path=":cloud/:accountId/revenue"
          element={
            <LayoutWrapper
              title={'Revenue insights'}
              component={RevenueInsightsWrapperV2}
              actions={Milestones}
              customNavButton={customNav}
              isSidePanelOpened={false}
            />
          }
        />
        {/* Backward compatibility */}
        <Route
          path="revenue"
          element={
            <LayoutWrapper
              title={'Revenue insights'}
              component={RevenueInsightsWrapperV2}
              actions={Milestones}
              customNavButton={customNav}
              isSidePanelOpened={false}
            />
          }
        />

        {/* New subscription routes with cloud and accountId */}
        <Route
          path=":cloud/:accountId/subscriptions/edit"
          element={
            <LayoutWrapper
              title="Create agreement based offer"
              component={RenewOfferSubscriptionWrapper}
              isBack={true}
              actions={SelectedCloudIcon}
              isSidePanelOpened={false}
            />
          }
        />
        <Route
          path=":cloud/:accountId/subscriptions/:subscriptionId/edit"
          element={
            <LayoutWrapper
              title="Create agreement based offer"
              component={RenewOfferSubscriptionWrapper}
              isBack={true}
              actions={SelectedCloudIcon}
              isSidePanelOpened={false}
            />
          }
        />
        <Route
          path=":cloud/:accountId/subscriptions/:subscriptionId/edit/:agreementOfferId"
          element={
            <LayoutWrapper
              title="Create agreement based offer"
              component={RenewOfferSubscriptionWrapper}
              isBack={true}
              actions={SelectedCloudIcon}
              isSidePanelOpened={false}
            />
          }
        />
        <Route
          path=":cloud/:accountId/subscriptions/:subscriptionId/authorization/:authorizationId/edit"
          element={
            <LayoutWrapper
              title="Create agreement based offer"
              component={RenewChannelOfferSubscription}
              isBack={true}
              actions={SelectedCloudIcon}
              isSidePanelOpened={false}
            />
          }
        />
        <Route
          path=":cloud/:accountId/subscriptions/:subscriptionId/authorization/:authorizationId/edit/:agreementOfferId"
          element={
            <LayoutWrapper
              title="Create agreement based offer"
              component={RenewChannelOfferSubscription}
              isBack={true}
              actions={SelectedCloudIcon}
              isSidePanelOpened={false}
            />
          }
        />
        <Route
          path=":cloud/:accountId/subscriptions"
          element={
            <LayoutWrapper
              title="Contracts"
              component={SubscriptionsList}
              isSidePanelOpened={false}
              actions={Milestones}
              customNavButton={customNav}
            />
          }
        />
        <Route
          path=":cloud/:accountId/subscriptions/:subscriptionsId/detail"
          element={
            <NoHeaderLayoutWrapper
              component={SubscriptionDetailWrapper}
              isSidePanelOpened={false}
            />
          }
        />

        {/* Backward compatibility routes */}
        <Route
          path="subscriptions/edit"
          element={
            <LayoutWrapper
              title="Create agreement based offer"
              component={RenewOfferSubscriptionWrapper}
              isBack={true}
              actions={SelectedCloudIcon}
              isSidePanelOpened={false}
            />
          }
        />
        <Route
          path="subscriptions/:subscriptionId/edit"
          element={
            <LayoutWrapper
              title="Create agreement based offer"
              component={RenewOfferSubscriptionWrapper}
              isBack={true}
              actions={SelectedCloudIcon}
              isSidePanelOpened={false}
            />
          }
        />
        <Route
          path="subscriptions/:subscriptionId/edit/:agreementOfferId"
          element={
            <LayoutWrapper
              title="Create agreement based offer"
              component={RenewOfferSubscriptionWrapper}
              isBack={true}
              actions={SelectedCloudIcon}
              isSidePanelOpened={false}
            />
          }
        />
        <Route
          path="subscriptions/:subscriptionId/authorization/:authorizationId/edit"
          element={
            <LayoutWrapper
              title="Create agreement based offer"
              component={RenewChannelOfferSubscription}
              isBack={true}
              actions={SelectedCloudIcon}
              isSidePanelOpened={false}
            />
          }
        />
        <Route
          path="subscriptions/:subscriptionId/authorization/:authorizationId/edit/:agreementOfferId"
          element={
            <LayoutWrapper
              title="Create agreement based offer"
              component={RenewChannelOfferSubscription}
              isBack={true}
              actions={SelectedCloudIcon}
              isSidePanelOpened={false}
            />
          }
        />
        <Route
          path="subscriptions"
          element={
            <LayoutWrapper
              title="Contracts"
              component={SubscriptionsList}
              isSidePanelOpened={false}
              actions={Milestones}
              customNavButton={customNav}
            />
          }
        />
        <Route
          path="subscriptions/:subscriptionsId/detail"
          element={
            <NoHeaderLayoutWrapper
              component={SubscriptionDetailWrapper}
              isSidePanelOpened={false}
            />
          }
        />
        <Route
          path=":cloud/:accountId/products-listing/edit"
          element={
            <NoHeaderLayoutWrapper
              component={AddEditProductListingWrapper}
              actions={SelectedCloudIcon}
              isSidePanelOpened={false}
            />
          }
        />
        {/* Backward compatibility */}
        <Route
          path="products-listing/edit"
          element={
            <NoHeaderLayoutWrapper
              component={AddEditProductListingWrapper}
              actions={SelectedCloudIcon}
              isSidePanelOpened={false}
            />
          }
        />
        <Route
          path=":cloud/:accountId/products-listing/:productId/edit"
          element={
            <NoHeaderLayoutWrapper
              component={AddEditProductListingWrapper}
              actions={SelectedCloudIcon}
              isSidePanelOpened={false}
            />
          }
        />
        {/* Backward compatibility */}
        <Route
          path="products-listing/:productId/edit"
          element={
            <NoHeaderLayoutWrapper
              component={AddEditProductListingWrapper}
              actions={SelectedCloudIcon}
              isSidePanelOpened={false}
            />
          }
        />
        <Route
          path=":cloud/:accountId/products-listing/:productId/details"
          element={
            <NoHeaderLayoutWrapper
              component={DetailProductListing}
              actions={SelectedCloudIcon}
              isSidePanelOpened={false}
            />
          }
        />
        {/* Backward compatibility */}
        <Route
          path="products-listing/:productId/details"
          element={
            <NoHeaderLayoutWrapper
              component={DetailProductListing}
              actions={SelectedCloudIcon}
              isSidePanelOpened={false}
            />
          }
        />
        {/* New route with fixed aws and accountId */}
        <Route
          path="aws/:accountId/buyers"
          element={
            <LayoutWrapper
              title={'Buyers'}
              component={Buyers}
              isSidePanelOpened={false}
              actions={Milestones}
              customNavButton={customNav}
              bgColor={theme.palette.labraV2BoxShadow.dark}
            />
          }
        />
        <Route
          path="aws/:accountId/buyers/:buyerId"
          element={
            <LayoutWrapper
              title={<BuyerHeader />}
              component={BuyerDetails}
              isSidePanelOpened={false}
              actions={Milestones}
              customNavButton={customNav}
              bgColor={theme.palette.labraV2BoxShadow.dark}
            />
          }
        />
        {/* Backward compatibility */}
        <Route
          path="buyers"
          element={
            <LayoutWrapper
              title={'Buyers'}
              component={Buyers}
              isSidePanelOpened={false}
              actions={Milestones}
              customNavButton={customNav}
              bgColor={theme.palette.labraV2BoxShadow.dark}
            />
          }
        />
        <Route
          path="buyers/:buyerId"
          element={
            <LayoutWrapper
              title={<BuyerHeader />}
              component={BuyerDetails}
              isSidePanelOpened={false}
              actions={Milestones}
              customNavButton={customNav}
              bgColor={theme.palette.labraV2BoxShadow.dark}
            />
          }
        />
        <Route
          path="storefront/*"
          element={
            <LayoutWrapper
              title={'Studio'}
              component={FlyOutStorefrontV2}
              isSidePanelOpened={false}
              actions={Milestones}
              customNavButton={customNav}
              bgColor={theme.palette.backgroundV2.light}
            />
          }
        />

        <Route
          path="/storefront/builder/listing-card"
          element={
            <LayoutWrapper
              title={'Create new card design'}
              component={FlyOutStorefrontTemplateWrapper}
              isSidePanelOpened={false}
              isBack={true}
              actions={() => (
                <LayoutWrapperActions
                  actions={flyOutStorefrontTemplateActions}
                />
              )}
              customNavButton={customNav}
              onBack={() => navigate('storefront')}
            />
          }
        />

        <Route
          path="/storefront/builder/buttons"
          element={
            <LayoutWrapper
              title={'Create new button'}
              component={ButtonCreationFormikWrapper}
              isSidePanelOpened={false}
              isBack={true}
              actions={() => (
                <LayoutWrapperActions
                  actions={flyOutStorefrontTemplateActions}
                />
              )}
              customNavButton={customNav}
              onBack={() => navigate('storefront/builder')}
            />
          }
        />
        <Route
          path="/storefront/builder/buttons/:integrationId"
          element={
            <LayoutWrapper
              title={'Edit button'}
              component={ButtonCreationFormikWrapper}
              isSidePanelOpened={false}
              isBack={true}
              customNavButton={customNav}
              actions={() => (
                <LayoutWrapperActions
                  actions={flyOutStorefrontTemplateActions}
                />
              )}
              onBack={() => navigate('storefront/builder')}
            />
          }
        />

        <Route
          path={'/storefront/builder/listing-card/:listingCardId'}
          element={
            <LayoutWrapper
              title={'Create new card design'}
              component={FlyOutStorefrontTemplateWrapper}
              isSidePanelOpened={false}
              isBack={true}
              actions={() => (
                <LayoutWrapperActions
                  actions={flyOutStorefrontTemplateActions}
                />
              )}
              customNavButton={customNav}
              onBack={() => navigate('storefront')}
            />
          }
        />
        <Route
          path="search-ranking"
          element={
            <LayoutWrapper
              title={'Search ranking detail'}
              component={SearchRankingV2}
              isSidePanelOpened={false}
              actions={Milestones}
              customNavButton={customNav}
              bgColor={theme.palette.bg.main}
            />
          }
        />
        {/* New route with cloud and accountId */}
        <Route
          path=":cloud/:accountId/usage-metering"
          element={
            <LayoutWrapper
              title={'Usage metering'}
              component={FlyOutUsageMetering}
              isSidePanelOpened={false}
              actions={Milestones}
              customNavButton={customNav}
              bgColor={theme.palette.backgroundV2.light}
              paddingZero
            />
          }
        />
        {/* Backward compatibility */}
        <Route
          path="usage-metering"
          element={
            <LayoutWrapper
              title={'Usage metering'}
              component={FlyOutUsageMetering}
              isSidePanelOpened={false}
              actions={Milestones}
              customNavButton={customNav}
              bgColor={theme.palette.backgroundV2.light}
            />
          }
        />

        {/* New routes with cloud and accountId */}
        <Route
          path=":cloud/:accountId/authorization/:authorizationId/channel-offers/edit"
          element={<ChannelOfferEditWrapper />}
        />
        <Route
          path=":cloud/:accountId/authorization/:authorizationId/channel-offers/:channelOfferId/edit"
          element={<ChannelOfferEditWrapper />}
        />
        <Route
          path=":cloud/:accountId/authorization/:authorizationId/channel-offers/:channelOfferId/detail"
          element={
            <NoHeaderLayoutWrapper
              component={ViewChannelOffer}
              actions={SelectedCloudIcon}
              isSidePanelOpened={false}
              isBack
            />
          }
        />

        {/* Backward compatibility routes */}
        <Route
          path="authorization/:authorizationId/channel-offers/edit"
          element={<ChannelOfferEditWrapper />}
        />
        <Route
          path="authorization/:authorizationId/channel-offers/:channelOfferId/edit"
          element={<ChannelOfferEditWrapper />}
        />
        <Route
          path="authorization/:authorizationId/channel-offers/:channelOfferId/detail"
          element={
            <NoHeaderLayoutWrapper
              component={ViewChannelOffer}
              actions={SelectedCloudIcon}
              isSidePanelOpened={false}
              isBack
            />
          }
        />

        <Route path="*" element={<PageNotFound />} />
      </Routes>
    </FlyoutMilestonesContext.Provider>
  )
}
